<template>
  <label class="tu-filter-select" :class="{ 'tu-filter-select--set': isSet, 'tu-filter-select--compact': compact }">
    <span class="tu-filter-select__text">
      <span class="tu-filter-select__label">{{ label }}</span>
      <span class="tu-filter-select__value">{{ display }}</span>
    </span>
    <select :value="String(value == null ? '' : value)" :aria-label="ariaLabel || label" @change="onChange">
      <option v-if="clearable" value="">{{ placeholder }}</option>
      <option v-for="o in normalised" :key="o.value" :value="o.value">{{ o.label }}</option>
    </select>
    <md-icon class="tu-filter-select__chev">expand_more</md-icon>
  </label>
</template>

<script>
/**
 * A filter segment: the filter's name over its current setting, with a
 * chevron. The native select sits invisible on top, so keyboard and mobile
 * pickers work. Ported from transition_uni_listings/components/Tu/FilterSelect.vue.
 * `icon` is accepted for compatibility and no longer drawn.
 *
 * `options` takes plain strings or { value, label } / { value, text }.
 */
export default {
  name: 'TuFilterSelect',
  props: {
    value: { type: [String, Number], default: '' },
    label: { type: String, required: true },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'All' },
    icon: { type: String, default: '' },
    ariaLabel: { type: String, default: '' },
    clearable: { type: Boolean, default: true },
    /** inline one-line version for a results row ("Sort by  School name") */
    compact: { type: Boolean, default: false },
  },
  computed: {
    normalised() {
      return this.options
        .filter(o => o != null && (typeof o !== 'object' || String(o.value) !== ''))
        .map(o =>
          o && typeof o === 'object'
            ? { value: String(o.value), label: o.label || o.text }
            : { value: String(o), label: String(o) },
        );
    },
    display() {
      const hit = this.normalised.find(o => o.value === String(this.value == null ? '' : this.value));
      return hit ? hit.label : this.placeholder;
    },
    isSet() {
      return this.clearable && String(this.value == null ? '' : this.value) !== '';
    },
  },
  methods: {
    onChange(e) {
      this.$emit('change', e.target.value);
    },
  },
};
</script>

<style lang="scss" scoped>
.tu-filter-select {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 56px;
  margin: 0;
  padding: 0 14px 0 18px;
  cursor: pointer;
  transition: background 0.15s;
}
.tu-filter-select:hover {
  background: var(--tu-surface-2, #f5f6f8);
}
.tu-filter-select:focus-within {
  box-shadow: inset 0 0 0 2px var(--tu-blue, #3b86f5);
  border-radius: 12px;
}
.tu-filter-select__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
  line-height: 1.15;
  pointer-events: none;
}
.tu-filter-select__label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--tu-ink-mute, rgba(24, 24, 24, 0.55));
}
.tu-filter-select__value {
  font-size: 14.5px;
  font-weight: 600;
  color: var(--tu-ink, #181818);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tu-filter-select--set .tu-filter-select__value {
  color: var(--tu-blue-deep, #245fcc);
}
.tu-filter-select select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  appearance: none;
  cursor: pointer;
}
.tu-filter-select__chev {
  flex: none;
  margin: 0;
  font-size: 20px;
  color: var(--tu-ink-mute, rgba(24, 24, 24, 0.55));
  pointer-events: none;
}
/* inline variant for a results row */
.tu-filter-select--compact { min-height: 36px; padding: 0 8px; border-radius: 8px; gap: 6px; }
.tu-filter-select--compact .tu-filter-select__text { flex-direction: row; align-items: baseline; gap: 6px; }
.tu-filter-select--compact .tu-filter-select__label { font-size: 13px; font-weight: 500; letter-spacing: 0; text-transform: none; color: var(--tu-ink-mute, rgba(24, 24, 24, 0.55)); white-space: nowrap; }
.tu-filter-select--compact .tu-filter-select__value { font-size: 13.5px; font-weight: 700; }
.tu-filter-select--compact .tu-filter-select__chev { font-size: 18px; }
@media (max-width: 540px) {
  .tu-filter-select { padding: 0 10px 0 14px; min-height: 52px; }
  .tu-filter-select__value { font-size: 13.5px; }
}
</style>
